import Joyride, { Step } from "react-joyride";
import { getIsIe11 } from "../../../constants/constants";
import ErrorBoundary from "../Errors/ErrorBoundary";
import { useSettings } from "../../../contexts/SettingsManager/SettingsManager";
import { useGetPageFromPath } from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { Link } from "react-router-dom";
import { JOYRIDE_STYLES } from "./JOYRIDE_STYLES";
import { JOYRIDE_TIMELINE_STEPS } from "./STEPS_TIMELINE";
import { JoyrideContentStyles } from "./JoyrideContentStyles";
import { getJoyrideSerialProcessSteps } from "./getJoyrideSerialProcessSteps";
import {
  getJoyrideGridPageSteps,
  LOT_INFO_BAR_STEPS,
} from "./getJoyrideGridPageSteps";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
const LOCALE_ENGLISH = {
  back: "Back",
  close: "Close",
  last: "Done",
  next: "Next",
  skip: "Skip",
};
const LOCALE_FRENCH = {
  back: "Retour",
  close: "Fermer",
  last: "OK",
  next: "Suivant",
  skip: "Passer",
};

const DEFAULT_PROPS = {
  continuous: true,
  showSkipButton: true,
  spotlightClicks: true,
  disableScrolling: true,
  showProgress: true,
  locale: LOCALE_ENGLISH,
  styles: JOYRIDE_STYLES,
};

function useIsFrench() {
  const { languagePreference } = useSettings();
  return (
    languagePreference?.slice(0, 2) === "fr" ||
    window.location.search.includes("lng=fr")
  );
}

const isInternetExplorer = getIsIe11();

export const JoyrideGuidedTour = () => {
  const { setIsTourEnabled } = useSettings();
  const isFrench = useIsFrench();
  return (
    <ErrorBoundary small={true} id="JoyrideGuidedTour">
      <Joyride
        // https://docs.react-joyride.com/props
        steps={JOYRIDE_TIMELINE_STEPS}
        {...DEFAULT_PROPS}
        callback={data => {
          // scroll to the bottom for the time axis controls step
          if (
            [
              `.timeAxisControlsButtonsWrapper`,
              `.TimeAxisStyles`,
              `.processNameLinkWrapper`,
              `.timeAxisColumn`,
              `.timeAxisColumn path.outOfFocusPathLeft`,
            ].includes(data.step.target.toString())
          ) {
            window.scrollTo(0, document.body.scrollHeight);
          }
          if (
            [`[class*=TrendChartStyles]`].includes(data.step.target.toString())
          ) {
            window.scrollTo(0, 64);
          }
          // hack: set the spotlight position to fixed for fixed elements
          if (
            [`[aria-label="RightDrawer"] span`].includes(
              data.step.target.toString()
            )
          ) {
            // offset from right
            document.body.style.overflow = "hidden";
            setTimeout(() => {
              const tooltip = document.querySelector(
                ".react-joyride__tooltip"
              ) as HTMLElement;
              if (tooltip && tooltip.style) {
                tooltip.style["margin-left"] = "-5px";
              }
              document.body.style.overflow = "visible";
            }, 0);

            // scroll to top
            window.scrollTo(0, 0);
            setTimeout(() => {
              const spotlight = document.querySelector(
                ".react-joyride__spotlight"
              ) as HTMLElement;
              if (spotlight && spotlight.style) {
                spotlight.style.position = "fixed";
              }
            }, 100);
          }

          // disable the tour on skip or after the last step
          if (
            data.action === "skip" ||
            (data.step.placement === "center" && data.type === "tour:end")
          ) {
            setIsTourEnabled(false);
          }
        }}
        {...(isFrench ? LOCALE_FRENCH : {})}
      />
    </ErrorBoundary>
  );
};

export const GridLayoutPageGuidedTour = () => {
  const { setIsTourEnabled } = useSettings();
  const ckey = useCustomerKey();
  const [
    {
      general: { enableLotInfoModule },
    },
  ] = useFlightConfig();
  const { translate } = useFlightTranslation();
  const steps = getJoyrideGridPageSteps(ckey, enableLotInfoModule).map(step =>
    step.translationKey
      ? {
          ...step,
          content: (
            <JoyrideContentStyles>
              {translate(step.translationKey, undefined, {
                postProcess: "markdown-jsx",
              })}
            </JoyrideContentStyles>
          ),
        }
      : step
  );
  const isFrench = useIsFrench();
  return (
    <ErrorBoundary small={true} id="GridLayoutPageGuidedTour">
      <Joyride
        // https://docs.react-joyride.com/props
        steps={steps as Step[]}
        {...DEFAULT_PROPS}
        callback={data => {
          // disable the tour on skip or after the last step
          if (
            data.action === "skip" ||
            (data.step.placement === "center" && data.type === "tour:end")
          ) {
            setIsTourEnabled(false);
          }
        }}
        {...(isFrench ? LOCALE_FRENCH : {})}
      />
    </ErrorBoundary>
  );
};

export const SerialProcessGuidedTour = ({ setIsTourEnabled }) => {
  const { isTimelinePage } = useGetPageFromPath();
  const isFrench = useIsFrench();
  return (
    <ErrorBoundary small={true} id="SerialProcessGuidedTour">
      <Joyride
        // https://docs.react-joyride.com/props
        steps={getJoyrideSerialProcessSteps({ isTimelinePage })}
        {...DEFAULT_PROPS}
        callback={data => {
          document.body.style.overflow = "hidden";
          // scroll to the bottom for the timeline step
          if (data.action === "next" && data.step.target === `.groupMetrics`) {
            if (isInternetExplorer) {
              window.scrollTo(0, document.body.scrollHeight);
            } else {
              window.scrollTo({
                top: document.body.scrollHeight,
                behavior: "smooth",
              });
            }
          }

          // disable the tour on skip or after the last step
          if (
            data.action === "skip" ||
            data.action === "close" ||
            (data.step.placement === "center" && data.type === "tour:end")
          ) {
            document.body.style.overflow = "visible";
            setIsTourEnabled(false);
          }
        }}
        {...(isFrench ? LOCALE_FRENCH : {})}
      />
    </ErrorBoundary>
  );
};

export const WifiDisconnectedGuidedTour = ({ setIsTourEnabled }) => {
  const ckey = useCustomerKey();
  const isFrench = useIsFrench();
  return (
    <ErrorBoundary small={true} id="WifiDisconnectedGuidedTour">
      <Joyride
        // https://docs.react-joyride.com/props
        steps={[
          {
            target: `#root`,
            placement: "center" as any,
            disableBeacon: true,
            content: (
              <JoyrideContentStyles>
                <div>No internet</div>
                <div>Try:</div>
                <ul style={{ listStyle: "disc", paddingLeft: 28 }}>
                  <li>Checking the network cables, modem, and router</li>
                  <li>Reconnecting to Wi-Fi</li>
                  <li>
                    <Link to={`/${ckey}/help`} style={{ color: "blue" }}>
                      Contacting Raven
                    </Link>{" "}
                    for support
                  </li>
                </ul>
              </JoyrideContentStyles>
            ),
          },
        ]}
        continuous={true}
        showProgress={false}
        showSkipButton={false}
        spotlightClicks={true}
        disableScrolling={true}
        locale={{
          back: "Back",
          close: "Close",
          last: "Close",
          next: "Next",
          skip: "Skip",
        }}
        callback={data => {
          document.body.style.overflow = "hidden";

          // disable the tour on skip or after the last step
          if (
            data.action === "skip" ||
            data.action === "close" ||
            (data.step.placement === "center" && data.type === "tour:end")
          ) {
            document.body.style.overflow = "visible";
            setIsTourEnabled(false);
          }
        }}
        {...(isFrench ? LOCALE_FRENCH : {})}
      />
    </ErrorBoundary>
  );
};

export const WifiSpottyGuidedTour = ({ setIsTourEnabled }) => {
  const isFrench = useIsFrench();
  const ckey = useCustomerKey();
  return (
    <ErrorBoundary small={true} id="WifiSpottyGuidedTour">
      <Joyride
        // https://docs.react-joyride.com/props
        steps={[
          {
            target: `#root`,
            placement: "center" as any,
            disableBeacon: true,
            content: (
              <JoyrideContentStyles>
                <div>Internet connection speed is low</div>
                <div>Try:</div>
                <ul style={{ listStyle: "disc", paddingLeft: 28 }}>
                  <li>Checking the network cables, modem, and router</li>
                  <li>Reconnecting to Wi-Fi</li>
                  <li>
                    <Link to={`/${ckey}/help`} style={{ color: "blue" }}>
                      Contacting Raven
                    </Link>{" "}
                    for support
                  </li>
                </ul>
              </JoyrideContentStyles>
            ),
          },
        ]}
        {...DEFAULT_PROPS}
        callback={data => {
          document.body.style.overflow = "hidden";

          // disable the tour on skip or after the last step
          if (
            data.action === "skip" ||
            data.action === "close" ||
            (data.step.placement === "center" && data.type === "tour:end")
          ) {
            document.body.style.overflow = "visible";
            setIsTourEnabled(false);
          }
        }}
        {...(isFrench ? LOCALE_FRENCH : {})}
      />
    </ErrorBoundary>
  );
};

export const LotInfoBarGuidedTour = ({ setIsTourEnabled }) => {
  const isFrench = useIsFrench();
  const { translate } = useFlightTranslation();
  const content = translate(
    LOT_INFO_BAR_STEPS[0].translationKey || "",
    undefined,
    {
      postProcess: "markdown-jsx",
    }
  );
  return (
    <ErrorBoundary small={true} id="LotInfoBarGuidedTour">
      <Joyride
        // https://docs.react-joyride.com/props
        steps={[
          {
            ...LOT_INFO_BAR_STEPS[0],
            content: <JoyrideContentStyles>{content}</JoyrideContentStyles>,
            disableBeacon:
              true /* don't require clicking the beacon; show tooltip right away */,
          },
        ]}
        {...DEFAULT_PROPS}
        run={true}
        callback={data => {
          // disable the tour on skip or after the last step
          if (
            data.action === "skip" ||
            data.action === "close" ||
            data.action === "reset"
          ) {
            setIsTourEnabled(false);
          }
        }}
        {...(isFrench ? LOCALE_FRENCH : {})}
      />
    </ErrorBoundary>
  );
};
